import { ComponentProps, forwardRef } from "react";
import { BaseInputProps, RadioProps } from "../types";
import { STYLE_MAP } from "../styles";
import { UseFormWatch } from "react-hook-form";
import { FormFieldWrapper } from "../FormFieldWrapper";

export const Checkbox = forwardRef<
  HTMLInputElement,
  {
    margin?: `m-${string}` | `ml-${string}`;
    label?: string;
    labelClassName?: string;
    wrapperClassName?: string;
    wrapperProps?: ComponentProps<"div">;
  } & ComponentProps<"input">
>(
  (
    {
      margin = "ml-3",
      label,
      labelClassName,
      wrapperClassName,
      wrapperProps,
      ...inputProps
    },
    ref
  ) => {
    const inputId = `input-checkbox-${inputProps.name}`;
    return (
      <div className={wrapperClassName} {...wrapperProps}>
        {label && (
          <label
            className={`font-semibold ${labelClassName}`}
            htmlFor={inputId}
          >
            {label}
          </label>
        )}
        <input
          id={inputId}
          data-testid="ts-and-cs-checkbox"
          type="checkbox"
          className={`rounded h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${margin}`}
          ref={ref}
          {...inputProps}
        />
      </div>
    );
  }
);

export const FormCheckbox = forwardRef<
  HTMLInputElement,
  BaseInputProps & { disabled: boolean }
>(
  (
    {
      label,
      errors,
      description,
      className = "",
      helperText,
      mode = "light",
      variant = "filled",
      disabled = false,
      ...inputProps
    },
    ref
  ) => {
    const { name } = inputProps;

    return (
      <FormFieldWrapper
        {...{
          name,
          errors,
          label,
          helperText,
          mode
        }}
      >
        <div className={`${STYLE_MAP[mode][variant].primary} col-span-1`}>
          <div className="items-center flex gap-2">
            {!disabled && (
              <>
                <input
                  type="checkbox"
                  id={description}
                  ref={ref}
                  {...inputProps}
                />
                <div>{description}</div>
              </>
            )}

            {disabled && <div>{label}</div>}
          </div>
        </div>
      </FormFieldWrapper>
    );
  }
);
export const FormMultiSelectCheckbox = forwardRef<
  HTMLInputElement,
  RadioProps & {
    watch?: UseFormWatch<any>;
  }
>(
  (
    {
      watch,
      label,
      errors,
      description,
      descriptionType,
      className = "",
      radioOptions,
      helperText,
      mode = "light",
      variant = "filled",
      disabled = false,
      ...inputProps
    },
    ref
  ) => {
    const { name } = inputProps;

    const columns = radioOptions.length > 4 ? "grid-cols-2" : "grid-cols-1";

    const checkedValue: string[] = (watch && watch(name)) || [];

    return (
      <FormFieldWrapper
        {...{
          name,
          errors,
          descriptionType,
          helperText,
          mode,
          disabled
        }}
        description={
          disabled && checkedValue.length < 1
            ? "There are no selected items. To add items to the list please edit the form."
            : description
        }
        descriptionType="text"
        label={label}
      >
        <div className={"grid " + columns}>
          {radioOptions.map(({ value, label, description }) => {
            if (!label) return null;
            return (
              <div
                className={`${STYLE_MAP[mode][variant].primary} col-span-1`}
                key={value}
              >
                <div className="items-center flex gap-2">
                  {!disabled && (
                    <>
                      <input
                        {...inputProps}
                        type="checkbox"
                        value={value}
                        id={inputProps.name + value}
                        ref={ref}
                      />
                      <div>{label}</div>
                    </>
                  )}
                  {disabled && (
                    <div>
                      {checkedValue && checkedValue.includes(String(value))
                        ? label
                        : null}
                    </div>
                  )}
                </div>
                {description && (
                  <p
                    className={`${STYLE_MAP[mode]["text"].secondary} text-xs mt-1 `}
                  >
                    {description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </FormFieldWrapper>
    );
  }
);
